<template>
  <div class="booking-view">
    <section class="booking-card tw-p-4 lg:tw-p-8 tw-mb-4 lg:tw-mb-6">
      <div class="tw-flex tw-flex-col tw-gap-3 lg:tw-gap-5 lg:tw-flex-row lg:tw-items-end lg:tw-justify-between">
        <div>
          <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#8d6b37]">Выбор дат</div>
          <h2 class="tw-mt-2 lg:tw-mt-3 tw-text-2xl tw-font-light tw-leading-tight tw-text-[#2d2924] lg:tw-text-5xl">
            Выберите период проживания
          </h2>
        </div>
        <div class="tw-max-w-md tw-text-sm tw-leading-relaxed tw-text-[#675f54]">
          Календарь показывает доступные даты на ближайшие месяцы. После выбора диапазона ниже сразу пересчитается стоимость проживания.
        </div>
      </div>
      <div class="tw-mt-4 lg:tw-mt-8">
        <input ref="pickerEl" class="tw-hidden"/>
      </div>
      <div v-if="attributes.start_date.errorText.value" class="tw-mt-4 tw-rounded-2xl tw-bg-[#fff1f1] tw-px-4 tw-py-3 tw-text-sm tw-text-[#a11d1d]">
        {{ attributes.start_date.errorText.value }}
      </div>
      <div v-if="isCalculatingDates" class="tw-mt-5 tw-inline-flex tw-items-center tw-gap-3 tw-rounded-full tw-bg-[#f7efe1] tw-px-4 tw-py-2 tw-text-sm tw-text-[#6a5331]">
        <loading-spinner size="sm" />
        <span>Получаем цены по выбранным датам</span>
      </div>
      <div v-else-if="dateBreakdown.length > 0" class="tw-mt-6">
        <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#8d6b37]">Стоимость по ночам</div>
        <div class="tw-mt-4 tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
          <div
              v-for="item in dateBreakdown"
              :key="item.date"
              class="tw-rounded-3xl tw-border tw-border-solid tw-border-[#eadfcb] tw-bg-white tw-p-4"
          >
            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div>
                <div class="tw-text-sm tw-text-[#6c6459]">{{ formatDateLabel(item.date) }}</div>
                <div v-if="item.isOverride" class="tw-mt-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.12em] tw-text-[#b98a43]">
                  Спеццена
                </div>
              </div>
              <div class="tw-text-base tw-font-medium tw-text-[#2d2924]">{{ priceFormat(item.price) }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="booking-grid">
      <div class="tw-space-y-6">
        <section class="booking-card tw-p-4 lg:tw-p-8">
          <div>
            <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#8d6b37]">Гости и проживание</div>
            <div class="tw-mt-2 lg:tw-mt-3 tw-text-2xl lg:tw-text-3xl tw-font-light tw-text-[#2d2924]">Настройте формат отдыха</div>
          </div>
          <div class="tw-mt-5 lg:tw-mt-8">
            <div class="tw-text-lg tw-font-medium tw-text-[#2d2924]">
              Количество гостей
            </div>
            <div class="tw-mt-2 tw-max-w-3xl tw-text-sm tw-leading-relaxed tw-text-[#6c6459]">
              Размещение 2 гостей входит в стоимость, каждый последующий гость оплачивается отдельно. Вместимость - 4 гостя, дети до 10 лет бесплатно без предоставления спальных мест. Обязательно одному из гостей должно быть более 21 года.
            </div>
          </div>
          <div class="tw-space-y-3 tw-mt-5 lg:tw-mt-6">
            <line-service
                v-model="attributes.count_user.value"
                name="Гости старше 10 лет"
                :show-price="false"
                :required="attributes.count_user.required"
                :disabled="isSubmittingOrder"
                :min="attributes.count_user.min" :max="attributes.count_user.max"
            />
            <line-service
                v-model="attributes.count_children.value"
                name="Гости до 10 лет"
                name-description="(бесплатно)"
                description="максимально 2 чел."
                :show-price="false"
                :disabled="isSubmittingOrder"
                :min="attributes.count_children.min" :max="attributes.count_children.max"
            />
          </div>
        </section>

        <section class="booking-card tw-p-4 lg:tw-p-8">
          <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#8d6b37]">Дополнительно</div>
          <div class="tw-mt-2 lg:tw-mt-3 tw-text-2xl lg:tw-text-3xl tw-font-light tw-text-[#2d2924]">Добавьте нужные услуги</div>
          <div class="tw-space-y-3 tw-mt-5 lg:tw-mt-8">
            <line-service
                v-model="attributes.sauna.value"
                name="Сауна"
                description="цена за 1 час, 1 час бесплатный на разогрев"
                name-description="(минимум 2 часа)"
                :price="getServiceDisplayPrice('sauna')"
                :disabled="isSubmittingOrder"
                :min="attributes.sauna.min" :max="attributes.sauna.max"
            />
            <line-service
                v-model="attributes.bicycle.value"
                name="Велосипед"
                description="цена за 1 шт. на 1 сутки"
                :price="getServiceDisplayPrice('bicycle')"
                :disabled="isSubmittingOrder"
                :min="attributes.bicycle.min" :max="attributes.bicycle.max"
            />
            <line-service
                v-model="attributes.coal.value"
                name="Уголь"
                description="цена за 1 шт."
                :price="getServiceDisplayPrice('coal')"
                :disabled="isSubmittingOrder"
                :min="attributes.coal.min" :max="attributes.coal.max"
            />
            <line-service
                v-model="attributes.coal_lighter.value"
                name="Розжиг"
                name-description="(0.5 л.)"
                description="цена за 1 шт."
                :price="getServiceDisplayPrice('coal_lighter')"
                :disabled="isSubmittingOrder"
                :min="attributes.coal_lighter.min" :max="attributes.coal_lighter.max"
            />
            <line-service
                v-model="attributes.dog.value"
                name="Размещение с собакой"
                name-description="(вес до 10 кг.)"
                description="цена единоразово"
                :price="getServiceDisplayPrice('dog')"
                :disabled="isSubmittingOrder"
                :min="attributes.dog.min" :max="attributes.dog.max"
            />
          </div>
        </section>

        <section class="booking-card tw-p-4 lg:tw-p-8">
          <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#8d6b37]">Контакты</div>
          <div class="tw-mt-2 lg:tw-mt-3 tw-text-2xl lg:tw-text-3xl tw-font-light tw-text-[#2d2924]">Введите свои данные</div>
          <div class="tw-grid tw-gap-4 tw-mt-5 lg:tw-mt-8 lg:tw-grid-cols-2">
            <div class="booking-field">
              <div class="booking-label">ФИО</div>
              <div class="tw-relative">
                <input
                    v-model="attributes.name.value.value"
                    :required="attributes.name.required"
                    :disabled="isSubmittingOrder"
                    @input="clearAttributeError('name')"
                    type="text"
                    class="booking-input"
                />
                <div class="booking-error">{{ attributes.name.errorText.value }}</div>
              </div>
            </div>
            <div class="booking-field">
              <div class="booking-label">Телефон</div>
              <div class="tw-relative">
                <input
                    ref="phoneInputEl"
                    :required="attributes.phone.required"
                    :disabled="isSubmittingOrder"
                    type="text"
                    class="booking-input"
                />
                <div class="booking-error">{{ attributes.phone.errorText.value }}</div>
              </div>
            </div>
            <div class="booking-field">
              <div class="booking-label">Email</div>
              <div class="tw-relative">
                <input
                    v-model="attributes.email.value.value"
                    :required="attributes.email.required"
                    :disabled="isSubmittingOrder"
                    @input="clearAttributeError('email')"
                    type="text"
                    class="booking-input"
                />
                <div class="booking-error">{{ attributes.email.errorText.value }}</div>
              </div>
            </div>
            <div class="booking-field lg:tw-col-span-2">
              <div class="booking-label">Ваши пожелания</div>
              <div class="tw-relative">
                <textarea
                    v-model="attributes.user_comment.value.value"
                    :required="attributes.user_comment.required"
                    :disabled="isSubmittingOrder"
                    @input="clearAttributeError('user_comment')"
                    class="booking-input booking-input--textarea"
                ></textarea>
                <div class="booking-error">{{ attributes.user_comment.errorText.value }}</div>
              </div>
            </div>
          </div>

          <div v-if="errorsText.length > 0" class="tw-mt-6 tw-rounded-3xl tw-bg-[#fff1f1] tw-p-4 tw-text-sm tw-text-[#a11d1d]">
            <ul class="tw-list-disc tw-list-inside tw-space-y-1">
              <li v-for="errorText in errorsText">{{errorText}}</li>
            </ul>
          </div>

        </section>
      </div>

      <aside class="booking-aside tw-space-y-6">
        <div class="booking-summary">
          <div class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-[0.16em] tw-text-[#f4ead5]">Итоги бронирования</div>
          <div class="tw-mt-4 tw-text-3xl tw-font-light tw-leading-tight tw-text-white">
            Проверьте даты, сумму и подтвердите заказ
          </div>

          <div class="tw-mt-8 tw-space-y-4">
            <div class="summary-row">
              <div class="summary-label">Заезд</div>
              <div class="summary-value" v-if="getStartDateFormat()">{{ getStartDateFormat() }}</div>
              <div class="summary-empty" v-else>не выбран</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Выезд</div>
              <div class="summary-value" v-if="getEndDateFormat()">{{ getEndDateFormat() }}</div>
              <div class="summary-empty" v-else>не выбран</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Стоимость проживания</div>
              <div v-if="isCalculatingOrder" class="summary-loading">
                <loading-spinner size="sm" />
                <span>Пересчитываем</span>
              </div>
              <div v-else class="summary-value">{{ priceFormat(calculation.stay.total) }}</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Итоговая стоимость</div>
              <div v-if="isCalculatingOrder" class="summary-loading">
                <loading-spinner size="sm" />
                <span>Обновляем</span>
              </div>
              <div v-else class="summary-value">{{ priceFormat(calculation.summary.total) }}</div>
            </div>
            <div class="summary-row summary-row--accent">
              <div class="summary-label">Предоплата</div>
              <div v-if="isCalculatingOrder" class="summary-loading">
                <loading-spinner size="sm" />
                <span>Считаем</span>
              </div>
              <div v-else class="summary-value">{{ priceFormat(calculation.summary.payNow) }}</div>
            </div>
          </div>

          <div v-if="isPricingBusy" class="tw-mt-6 tw-inline-flex tw-items-center tw-gap-3 tw-rounded-full tw-bg-white tw-bg-opacity-10 tw-px-4 tw-py-2 tw-text-sm tw-text-[#f4ead5]">
            <loading-spinner size="sm" />
            <span>{{ pricingStatusText }}</span>
          </div>

          <div v-if="requestErrorText" class="tw-mt-6 tw-rounded-3xl tw-bg-[#fff6ec] tw-p-4 tw-text-sm tw-leading-relaxed tw-text-[#8a5522]">
            {{ requestErrorText }}
          </div>

          <div class="tw-mt-8 tw-rounded-3xl tw-border tw-border-solid tw-border-white tw-border-opacity-10 tw-bg-black tw-bg-opacity-10 tw-p-5">
            <div class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.14em] tw-text-[#f4ead5] tw-opacity-80">
              Важная информация
            </div>
            <div class="tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-white tw-opacity-78">
              Полная стоимость проживания с учетом выбранных услуг оплачивается при заселении. Залог возвращается перед выездом, если нет недочетов во время отдыха.
            </div>
            <div class="tw-mt-4 tw-border tw-border-0 tw-border-t tw-border-solid tw-border-white tw-border-opacity-10 tw-pt-4 tw-text-sm tw-leading-relaxed tw-text-[#d9c8a5] tw-opacity-90">
              Бронь можно отменить не раньше чем за 7 суток до отдыха, ранее залог не возвращается.
            </div>
          </div>

          <div class="tw-mt-8">
            <button
                @click="checkout()"
                :disabled="isCheckoutDisabled"
                type="button"
                class="booking-submit"
                :class="{'booking-submit--disabled': isCheckoutDisabled}"
            >
              <span v-if="isSubmittingOrder" class="tw-inline-flex tw-items-center tw-gap-3">
                <loading-spinner size="sm" />
                <span>Оформляем бронирование...</span>
              </span>
              <span v-else>{{ isPricingBusy ? 'Пересчитываем стоимость...' : 'Забронировать' }}</span>
            </button>
          </div>

          <div class="tw-mt-6 tw-text-xs tw-leading-relaxed tw-text-white tw-opacity-70">
            Фактом бронирования вы соглашаетесь с <a href="/site/rules-reservation" class="tw-text-[#f4ead5]" target="_blank">правилами и условиями бронирования</a>,
            <br/>
            <a href="/site/personal-data" class="tw-text-[#f4ead5]" target="_blank">обработкой персональных данных и политикой конфиденциальности</a>
          </div>
        </div>
      </aside>
    </div>
    <confirm-phone-modal :phone="attributes.phone.value" ref="confirmModal"/>
  </div>
</template>

<script setup>
import validator from 'validator';
import IMask from 'imask';
import dayjs from 'dayjs';
import {computed, defineProps, onBeforeUnmount, onMounted, ref, watch} from 'vue'

import HotelDatepicker from "hotel-datepicker";
import "hotel-datepicker/dist/css/hotel-datepicker.css";

import LineService from "./LineService.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import ConfirmPhoneModal from "../confirmPhone/ConfirmPhoneModal.vue";
import {priceFormat} from "./helper";

const props = defineProps([
  'attributesParams',
  'defaultTimeStart',
  'defaultTimeEnd',
  'busyDatesFuture',
  'sumPrepayment',
  'endpoints'
]);

const confirmModal = ref(null);
const pickerEl = ref(null);
const phoneInputEl = ref(null);
const calculationAttributeNames = [
  'start_date',
  'end_date',
  'count_user',
  'count_children',
  'sauna',
  'bicycle',
  'coal',
  'coal_lighter',
  'dog',
];
const checkoutAttributeNames = [
  ...calculationAttributeNames,
  'name',
  'phone',
  'email',
  'user_comment',
];
const additionalServiceAttributeNames = ['sauna', 'bicycle', 'coal', 'coal_lighter', 'dog'];

const createEmptyCalculation = () => ({
  dates: [],
  stay: {
    nights: 0,
    base: 0,
    guestSurchargePerNight: 0,
    guestSurcharge: 0,
    total: 0,
  },
  services: {
    items: additionalServiceAttributeNames.reduce((accumulator, attribute) => {
      accumulator[attribute] = {
        count: 0,
        unitPrice: props.attributesParams?.[attribute]?.price || 0,
        total: 0,
      };

      return accumulator;
    }, {}),
    total: 0,
  },
  summary: {
    total: 0,
    prepayment: 0,
    payNow: 0,
  }
});

const attributes = {
  start_date: {
    value: ref(null),
    errorText: ref(null),
    validate(attribute) {
      if (!attribute.value.value) {
        attribute.errorText.value = 'Выберите дату заезда';
      }

      return attribute.errorText.value === null;
    }
  },
  end_date:  {
    value: ref(null),
    errorText: ref(null),
    validate(attribute) {
      if (!attribute.value.value) {
        attribute.errorText.value = 'Выберите дату выезда';
      }

      return attribute.errorText.value === null;
    }
  },
  count_user: {
    value: ref(1),
    min: props.attributesParams.count_user.min,
    max: props.attributesParams.count_user.max,
    required: props.attributesParams.count_user.required
  },
  count_children: Object.assign({}, {value: ref(0)}, props.attributesParams.count_children),
  sauna: Object.assign({}, {value: ref(0)}, props.attributesParams.sauna),
  bicycle: Object.assign({}, {value: ref(0)}, props.attributesParams.bicycle),
  coal: Object.assign({}, {value: ref(0)}, props.attributesParams.coal),
  coal_lighter: Object.assign({}, {value: ref(0)}, props.attributesParams.coal_lighter),
  dog: Object.assign({}, {value: ref(0)}, props.attributesParams.dog),
  name: {
    value: ref(null),
    required: true,
    errorText: ref(null),
    validate: (attribute) => {
      if (validator.isLength(attribute.value.value || '', {max: 0})) {
        attribute.errorText.value = 'Обязательно поле';
      }
      if (validator.isLength(attribute.value.value || '', {min: 255})) {
        attribute.errorText.value = 'Превышен размер';
      }
      return attribute.errorText.value === null;
    }
  },
  email: {
    value: ref(null),
    required: false,
    errorText: ref(null),
    validate(attribute) {
      if (attribute.value.value && !validator.isEmail(attribute.value.value || '')) {
        attribute.errorText.value = 'Не корректное значение';
      }
      return attribute.errorText.value === null;
    }
  },
  phone: {
    value: ref(null),
    required: true,
    errorText: ref(null),
    validate(attribute) {
      if (validator.isLength(attribute.value.value || '', {max: 0})) {
        attribute.errorText.value = 'Обязательно поле';
      }
      if (!/(^8|7|\+7)((\d{10})|(\s\(\d{3}\)\s\d{3}\s\d{2}\s\d{2}))/.test(attribute.value.value || '')) {
        attribute.errorText.value = 'Не корректное значение';
      }
      return attribute.errorText.value === null;
    }
  },
  user_comment: {
    value: ref(null),
    required: false,
    errorText: ref(null),
    validate(attribute) {
      if (!validator.isLength(attribute.value.value || '', {max: 1024})) {
        attribute.errorText.value = 'Превышен размер';
      }
      return attribute.errorText.value === null;
    }
  },
}
const dateBreakdown = ref([]);
const calculation = ref(createEmptyCalculation());
const isCalculationReady = ref(false);
const errorsText = ref([]);
const requestErrorText = ref(null);
const isCalculatingDates = ref(false);
const isCalculatingOrder = ref(false);
const isSubmittingOrder = ref(false);
const isMounted = ref(false);
let datePricesController = null;
let calculationController = null;
let datePricesRequestId = 0;
let calculationRequestId = 0;

const isPricingBusy = computed(() => isCalculatingDates.value || isCalculatingOrder.value);
const pricingStatusText = computed(() => {
  if (isCalculatingDates.value) return 'Обновляем цены по выбранным датам';
  if (isCalculatingOrder.value) return 'Пересчитываем итоговую стоимость';

  return '';
});
const isCheckoutDisabled = computed(() => {
  return (
      !isCalculationReady.value
      || isPricingBusy.value
      || isSubmittingOrder.value
  );
});

const getPayload = (attributeNames) => {
  const payload = {};
  attributeNames.forEach((attribute) => {
    payload[attribute] = attributes[attribute].value.value;
  });

  return payload;
};

const getStartDateFormat = () => {
  if (attributes.start_date.value.value === null) return null;
  return dayjs(attributes.start_date.value.value).format('DD.MM.YYYY') + ' в ' + props.defaultTimeStart;
}
const getEndDateFormat = () => {
  if (attributes.end_date.value.value === null) return null;
  return dayjs(attributes.end_date.value.value).format('DD.MM.YYYY') + ' в ' + props.defaultTimeEnd;
}

const formatDateLabel = (date) => {
  return dayjs(date).format('DD.MM.YYYY');
};

const getStaticServiceDisplayPrice = (attributeName) => {
  const count = attributes[attributeName].value.value || 0;
  const unitPrice = props.attributesParams?.[attributeName]?.price || 0;

  if (count > 0) {
    return unitPrice * count;
  }

  return unitPrice;
};

const getServiceDisplayPrice = (attributeName) => {
  const item = calculation.value.services?.items?.[attributeName];
  const selectedCount = attributes[attributeName].value.value || 0;

  if (item && item.count > 0 && item.count === selectedCount) {
    return item.total;
  }

  return getStaticServiceDisplayPrice(attributeName);
};

const clearAttributeError = (attributeName) => {
  if (attributes[attributeName]?.errorText !== undefined) {
    attributes[attributeName].errorText.value = null;
  }
};

const clearCommonErrors = () => {
  errorsText.value = [];
  requestErrorText.value = null;
};

const clearPricingErrors = () => {
  clearAttributeError('start_date');
  clearAttributeError('end_date');
  clearCommonErrors();
};

const clearAllErrors = () => {
  Object.keys(attributes).forEach((attribute) => clearAttributeError(attribute));
  clearCommonErrors();
};

const applyErrors = (errors, clearFn = clearCommonErrors) => {
  clearFn();

  Object.entries(errors || {}).forEach(([attribute, errorText]) => {
    if (!errorText) return;

    if (attributes[attribute] !== undefined && attributes[attribute].errorText !== undefined) {
      attributes[attribute].errorText.value = errorText;
      return;
    }

    errorsText.value.push(errorText);
  });
};

const resetCalculationState = () => {
  calculation.value = createEmptyCalculation();
  isCalculationReady.value = false;
};

const resetDateSelectionState = () => {
  dateBreakdown.value = [];
  resetCalculationState();
};

const validateCheckout = () => {
  clearAllErrors();

  let result = true;
  ['start_date', 'end_date', 'name', 'email', 'phone', 'user_comment'].forEach((attribute) => {
    if (attributes[attribute].validate !== undefined && !attributes[attribute].validate(attributes[attribute])) {
      result = false;
    }
  });

  return result;
};

const setRequestError = (message) => {
  requestErrorText.value = message;
};

const createRequestErrorMessage = (fallbackMessage) => {
  return fallbackMessage || 'Не удалось связаться с сервером. Попробуйте еще раз.';
};

const postJson = async (url, payload, signal) => {
  const options = {
    method: 'post',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json'
    },
  };

  if (signal) {
    options.signal = signal;
  }

  const response = await fetch(url, options);

  let result = null;
  try {
    result = await response.json();
  } catch (error) {
    throw new Error('Сервер вернул некорректный ответ. Обновите страницу и попробуйте еще раз.');
  }

  if (!response.ok) {
    throw new Error(result?.message || 'Сервер временно недоступен. Попробуйте еще раз.');
  }

  return result;
};

const refreshDatePrices = async () => {
  if (!attributes.start_date.value.value || !attributes.end_date.value.value) {
    resetDateSelectionState();
    return false;
  }

  if (datePricesController) {
    datePricesController.abort();
  }

  datePricesRequestId += 1;
  const requestId = datePricesRequestId;
  datePricesController = new AbortController();
  isCalculatingDates.value = true;
  clearPricingErrors();

  try {
    const result = await postJson(
        props.endpoints.datePrices,
        getPayload(['start_date', 'end_date']),
        datePricesController.signal
    );

    if (requestId !== datePricesRequestId) {
      return false;
    }

    if (!result.success) {
      dateBreakdown.value = [];
      resetCalculationState();
      applyErrors(result.errors, clearPricingErrors);

      return false;
    }

    dateBreakdown.value = result.data?.dates || [];
    return true;
  } catch (error) {
    if (error.name === 'AbortError') {
      return false;
    }

    resetDateSelectionState();
    setRequestError(createRequestErrorMessage(error.message));
    return false;
  } finally {
    if (requestId === datePricesRequestId) {
      isCalculatingDates.value = false;
    }
  }
};

const refreshCalculation = async () => {
  if (!attributes.start_date.value.value || !attributes.end_date.value.value) {
    resetCalculationState();
    return false;
  }

  if (calculationController) {
    calculationController.abort();
  }

  calculationRequestId += 1;
  const requestId = calculationRequestId;
  calculationController = new AbortController();
  isCalculatingOrder.value = true;
  clearPricingErrors();

  try {
    const result = await postJson(
        props.endpoints.calculate,
        getPayload(calculationAttributeNames),
        calculationController.signal
    );

    if (requestId !== calculationRequestId) {
      return false;
    }

    if (!result.success) {
      resetCalculationState();
      applyErrors(result.errors, clearPricingErrors);

      return false;
    }

    calculation.value = result.data || createEmptyCalculation();
    dateBreakdown.value = result.data?.dates || [];
    isCalculationReady.value = true;
    return true;
  } catch (error) {
    if (error.name === 'AbortError') {
      return false;
    }

    resetCalculationState();
    setRequestError(createRequestErrorMessage(error.message));
    return false;
  } finally {
    if (requestId === calculationRequestId) {
      isCalculatingOrder.value = false;
    }
  }
};

const refreshPricing = async () => {
  const hasDatePrices = await refreshDatePrices();
  if (!hasDatePrices) {
    return false;
  }

  return refreshCalculation();
};

const checkout = async () => {
  if (!validateCheckout()) return;
  if (isCheckoutDisabled.value) return;

  isSubmittingOrder.value = true;
  clearAllErrors();

  try {
    const result = await postJson(
        props.endpoints.save,
        getPayload(checkoutAttributeNames),
        null
    );

    if (result.errors !== undefined && Object.keys(result.errors).length > 0) {
      applyErrors(result.errors, clearAllErrors);
    }

    if (result.type !== undefined && result.type === 'confirmContact') {
      confirmModal.value.open();
      return;
    }

    if (result.type !== undefined && result.type === 'checkout' && result.success === true) {
      window.location = result.data.urlRedirect;
      return;
    }

    if (!result.success && (!result.errors || Object.keys(result.errors).length === 0)) {
      setRequestError('Не удалось оформить бронирование. Попробуйте еще раз.');
    }
  } catch (error) {
    setRequestError(createRequestErrorMessage(error.message));
  } finally {
    isSubmittingOrder.value = false;
  }
};

onMounted(() => {
  isMounted.value = true;

  const maskOptions = {
    mask: '+{7}(000)000-00-00'
  };
  const mask = IMask(phoneInputEl.value, maskOptions);
  mask.on('accept', () => {
    attributes.phone.value.value = mask.unmaskedValue;
    clearAttributeError('phone');
  });

  let picker = new HotelDatepicker(pickerEl.value, {
    inline: true,
    disabledDates: props.busyDatesFuture,
    startDate: new Date(),
    endDate: dayjs().add(6, 'month').endOf('month').toDate(),
    enableCheckout: true,
    showTopbar: false,
    startOfWeek: 'monday',
    hoveringTooltip: function (nights, startTime, hoverTime) {
      return nights;
    },
    onDayClick() {
      attributes.start_date.value.value = null;
      attributes.end_date.value.value = null;
      clearAttributeError('start_date');
      clearAttributeError('end_date');
      clearCommonErrors();
      resetDateSelectionState();

      if (picker.start && picker.end) {
        const pickerDates = picker.getValue().split(picker.separator);
        attributes.start_date.value.value = dayjs(pickerDates[0]).format('YYYY-MM-DD');
        attributes.end_date.value.value = dayjs(pickerDates[1]).format('YYYY-MM-DD');
        refreshPricing();
      }
    },
    i18n: {
      night: 'Ночь',
      nights: 'Ночей',
      'day-names-short': ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
      'day-names': ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
      'month-names-short': ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
      'month-names': ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
      selected: 'Your stay:',
      button: 'Close',
      clearButton: 'Clear',
      submitButton: 'Submit',
      'checkin-disabled': 'Check-in disabled',
      'checkout-disabled': 'Check-out disabled',
      'error-more': 'Date range should not be more than 1 night',
      'error-more-plural': 'Date range should not be more than %d nights',
      'error-less': 'Date range should not be less than 1 night',
      'error-less-plural': 'Date range should not be less than %d nights',
      'info-more': 'Please select a date range of at least 1 night',
      'info-more-plural': 'Please select a date range of at least %d nights',
      'info-range': 'Please select a date range between %d and %d nights',
      'info-range-equal': 'Please select a date range of %d nights',
      'info-default': 'Please select a date range',
      'aria-application': 'Calendar',
      'aria-selected-checkin': 'Selected as check-in date, %s',
      'aria-selected-checkout': 'Selected as check-out date, %s',
      'aria-selected': 'Selected, %s',
      'aria-disabled': 'Not available, %s',
      'aria-choose-checkin': 'Choose %s as your check-in date',
      'aria-choose-checkout': 'Choose %s as your check-out date',
      'aria-prev-month': 'Move backward to switch to the previous month',
      'aria-next-month': 'Move forward to switch to the next month',
      'aria-close-button': 'Close the datepicker',
      'aria-clear-button': 'Clear the selected dates',
      'aria-submit-button': 'Submit the form'
    }
  });
});

watch(
    () => [
      attributes.count_user.value.value,
      attributes.count_children.value.value,
      attributes.sauna.value.value,
      attributes.bicycle.value.value,
      attributes.coal.value.value,
      attributes.coal_lighter.value.value,
      attributes.dog.value.value,
    ],
    () => {
      if (!isMounted.value) {
        return;
      }

      if (!attributes.start_date.value.value || !attributes.end_date.value.value) {
        resetCalculationState();
        return;
      }

      refreshCalculation();
    }
);

onBeforeUnmount(() => {
  if (datePricesController) {
    datePricesController.abort();
  }

  if (calculationController) {
    calculationController.abort();
  }
});
</script>

<style>

.booking-view {
  color: #2d2924;
}

.booking-grid {
  display: grid;
  gap: 24px;
}

.booking-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(161, 128, 76, 0.14);
  border-radius: 24px;
  box-shadow: 0 22px 60px rgba(89, 62, 30, 0.08);
}

.booking-card .datepicker__wrapper {
  width: 100%;
  overflow-x: auto;
}

.booking-aside {
  position: sticky;
  top: 0px;
  align-self: start;
}

.booking-summary {
  position: sticky;
  background: linear-gradient(180deg, rgba(18, 18, 18, 0.96), rgba(11, 11, 11, 0.88));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
  padding: 28px;
}

.booking-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.booking-label {
  font-size: 14px;
  font-weight: 600;
  color: #4b4339;
}

.booking-input {
  width: 100%;
  min-height: 58px;
  border-radius: 20px;
  border: 1px solid #d8cfbf;
  background: rgba(255, 255, 255, 0.96);
  padding: 16px 18px;
  color: #201c18;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.booking-input:focus {
  border-color: #b98a43;
  box-shadow: 0 0 0 4px rgba(185, 138, 67, 0.14);
}

.booking-input:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.booking-input--textarea {
  min-height: 140px;
  resize: vertical;
}

.booking-error {
  margin-top: 6px;
  font-size: 12px;
  color: #a11d1d;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.summary-row--accent {
  padding-top: 6px;
}

.summary-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.62);
}

.summary-value {
  max-width: 220px;
  text-align: right;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
}

.summary-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(244, 234, 213, 0.9);
}

.summary-empty {
  color: rgba(255, 255, 255, 0.4);
}

.booking-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 56px;
  border: 0;
  border-radius: 999px;
  background: #f4ead5;
  color: #111111;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.booking-submit:hover {
  transform: translateY(-1px);
}

.booking-submit--disabled {
  background: #8e877c;
  color: rgba(255, 255, 255, 0.88);
  cursor: not-allowed;
  transform: none;
}

.datepicker {
  width: 100% !important;
  max-width: 100%;
  border-radius: 24px;
  background: #fffdf9;
  box-shadow: none !important;
}

.datepicker__month {
  width: calc(50% - 24px);
  margin: 0 20px;
}

.datepicker__inner {
  padding: 20px 0 !important;
}

.datepicker__months {
  display: flex;
  align-items: flex-start;
}

.datepicker__month-name {
  font-size: 28px;
  font-weight: 300;
  color: #2d2924;
}

.datepicker__month-day--invalid {
  color: #bbbdc4 !important;
}

.datepicker__month-day {
  border: 0 !important;
  padding: 15px 10px !important;
  border-radius: 12px;
}

.datepicker__month-day--disabled:after {
  content: '';
}

.datepicker__month-day--disabled, .datepicker__month-day--invalid {
  background: #f2f1f0;
  color: #a3a3a3;
}

.datepicker__month-day--selected,
.datepicker__month-day--first-day-selected,
.datepicker__month-day--last-day-selected {
  background: #171513 !important;
  color: #fff !important;
}

.datepicker__month-button {
  color: #2d2924;
}

@media (max-width: 780px) {
  .booking-grid {
    gap: 16px;
  }

  .booking-card {
    border-radius: 20px;
    box-shadow: 0 14px 36px rgba(89, 62, 30, 0.08);
  }

  .booking-aside {
    position: static;
    top: auto;
  }

  .booking-summary {
    position: static;
    top: auto;
    padding: 18px;
    border-radius: 20px;
  }

  .summary-row {
    flex-direction: column;
    gap: 6px;
  }

  .summary-value {
    max-width: none;
    text-align: left;
  }

  .datepicker__inner {
    padding: 0;
  }

  .datepicker__months {
    display: block !important;
  }

  .datepicker__month {
    width: 100% !important;
    margin: 0 !important;
    min-width: 0 !important;
  }

  .datepicker__months:before {
    display: none !important;
  }

  .datepicker__month--month1 {
    margin-bottom: 12px !important;
  }
}

@media (max-width: 640px) {
  .datepicker {
    width: 100% !important;
    min-width: 0 !important;
    border-radius: 18px;
    overflow: hidden;
  }

  .datepicker__wrapper {
    min-width: 0 !important;
  }

  .datepicker__inner,
  .datepicker__months,
  .datepicker__month {
    max-width: 100% !important;
  }

  .datepicker__month table {
    width: 100% !important;
    table-layout: fixed;
  }

  .datepicker__month th,
  .datepicker__month td {
    width: 14.285%;
  }

  .datepicker__month--month2 {
    display: none !important;
  }

  .datepicker__month-name {
    font-size: 20px;
  }

  .datepicker__month-caption {
    height: auto;
    padding: 0 0 6px;
  }

  .datepicker__month-button {
    padding: 0 8px !important;
  }

  .datepicker__month-day {
    min-width: 0 !important;
    height: 36px !important;
    padding: 0 !important;
    border-radius: 10px;
    font-size: 12px;
    line-height: 36px !important;
  }
}

@media (min-width: 768px) {
  .booking-grid {
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    align-items: start;
  }

  .datepicker {
    width: 100% !important;
  }

  .datepicker__month {
    width: calc(50% - 24px) !important;
  }
}

@media (min-width: 1024px) {
  .booking-card {
    border-radius: 32px;
  }

  .datepicker {
    border-radius: 28px;
  }
}

</style>
